/**
 * PVJ Wishlist
 * ------------
 * A self-contained wishlist with no app and no backend. Saved pieces live in
 * the visitor's own browser (localStorage), so nothing is stored on Shopify
 * and no customer login is required.
 *
 * One list is the single source of truth. Every change fires a
 * `pvj:wishlist:change` event on `document`, and the bookmark buttons, the
 * header counter and the wishlist page all re-sync from that event -
 * including across browser tabs.
 *
 * Public API: window.PVJWishlist
 *   .items()              -> [{ handle, title, addedAt }]
 *   .handles()            -> ['ring-a', 'ring-b']
 *   .count()              -> Number
 *   .has(handle)          -> Boolean
 *   .add(handle, meta)    -> Boolean (false if it was already saved)
 *   .remove(handle)       -> Boolean
 *   .toggle(handle, meta) -> Boolean (true when it ends up saved)
 *   .addMany(handles)     -> Number added
 *   .clear()              -> void
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'pvj:wishlist';
  var CHANGE_EVENT = 'pvj:wishlist:change';
  var MAX_ITEMS = 100;
  var TILE_SECTION_ID = 'api-wishlist-item';
  var FETCH_CONCURRENCY = 6;

  // Matches the opacity/height transition durations declared in
  // pvj-wishlist.css for .is-removing / .is-fading-out / .is-resizing.
  // Skipped for visitors who asked their OS for less motion, rather than
  // making them wait out a fade they can't see.
  var REDUCE_MOTION = Boolean(
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  var TILE_FADE_MS = REDUCE_MOTION ? 0 : 280;
  var EMPTY_FADE_OUT_MS = REDUCE_MOTION ? 0 : 280;
  var RESIZE_MS = REDUCE_MOTION ? 0 : 320;

  // Matches the width requested in snippets/wishlist-button.liquid for the
  // popover image, so a variant swapped in client-side is the same size as
  // one baked in by Liquid on first render.
  var POPOVER_IMAGE_WIDTH = 240;

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  function each(list, callback) {
    Array.prototype.forEach.call(list || [], callback);
  }

  function isValidHandle(handle) {
    return typeof handle === 'string' && /^[a-z0-9][a-z0-9_-]{0,59}$/i.test(handle);
  }

  // Shopify's CDN resizes any image URL on request, so this works whether
  // the src Liquid handed us already carries a size or is untouched.
  function resizeImage(src, width) {
    if (!src) return src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  // Reuses the theme's own money formatter/settings (assets/theme.js) so a
  // variant swapped in client-side reads exactly like a page-rendered price -
  // same currency_code_enable rule as snippets/wishlist-button.liquid. Falls
  // back to null (leaving whatever price is already on the button) if the
  // theme hasn't exposed that helper for some reason.
  function formatVariantPrice(cents) {
    var themeGlobal = window.theme;
    if (!themeGlobal || typeof themeGlobal.formatMoney !== 'function') return null;

    var format = themeGlobal.settings && themeGlobal.settings.currencyCodeEnable
      ? themeGlobal.moneyWithCurrencyFormat
      : themeGlobal.moneyWithoutCurrencyFormat;

    return themeGlobal.formatMoney(cents, format);
  }

  /* ------------------------------------------------------------------ *
   * Store
   * ------------------------------------------------------------------ */

  function readStore() {
    var parsed;

    try {
      parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (error) {
      return [];
    }

    if (!Array.isArray(parsed)) return [];

    var seen = {};

    return parsed
      .filter(function (item) {
        if (!item || !isValidHandle(item.handle) || seen[item.handle]) return false;
        seen[item.handle] = true;
        return true;
      })
      .slice(0, MAX_ITEMS);
  }

  var items = readStore();

  function emit() {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { items: items.slice() } })
    );
  }

  function persist() {
    items = items.slice(0, MAX_ITEMS);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // Private browsing or a full quota. The list still works for this page
      // view, it just will not survive a reload - better than throwing.
    }

    emit();
  }

  function indexOfHandle(handle) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].handle === handle) return i;
    }
    return -1;
  }

  var api = {
    STORAGE_KEY: STORAGE_KEY,
    CHANGE_EVENT: CHANGE_EVENT,

    items: function () {
      return items.slice();
    },

    handles: function () {
      return items.map(function (item) {
        return item.handle;
      });
    },

    count: function () {
      return items.length;
    },

    has: function (handle) {
      return indexOfHandle(handle) > -1;
    },

    add: function (handle, meta) {
      if (!isValidHandle(handle) || api.has(handle)) return false;

      // Newest first, so the wishlist page reads like a recent-saves list.
      items.unshift({
        handle: handle,
        title: (meta && meta.title) || '',
        variantId: (meta && meta.variantId) || null,
        addedAt: Date.now()
      });

      persist();
      return true;
    },

    remove: function (handle) {
      var index = indexOfHandle(handle);
      if (index === -1) return false;

      items.splice(index, 1);
      persist();
      return true;
    },

    toggle: function (handle, meta) {
      if (api.has(handle)) {
        api.remove(handle);
        return false;
      }

      api.add(handle, meta);
      return true;
    },

    addMany: function (handles) {
      var added = 0;

      (handles || []).forEach(function (handle) {
        if (!isValidHandle(handle) || api.has(handle)) return;
        items.unshift({ handle: handle, title: '', addedAt: Date.now() });
        added++;
      });

      if (added) persist();
      return added;
    },

    clear: function () {
      if (!items.length) return;
      items = [];
      persist();
    }
  };

  window.PVJWishlist = api;

  // Another tab changed the list.
  window.addEventListener('storage', function (event) {
    if (event.key && event.key !== STORAGE_KEY) return;
    items = readStore();
    emit();
  });

  /* ------------------------------------------------------------------ *
   * Screen reader announcements
   * ------------------------------------------------------------------ */

  var announcer = null;

  function announce(message) {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      document.body.appendChild(announcer);
    }

    // Clearing first makes a repeat of the same message speak again.
    announcer.textContent = '';
    window.setTimeout(function () {
      announcer.textContent = message;
    }, 60);
  }

  /* ------------------------------------------------------------------ *
   * Header counter
   * ------------------------------------------------------------------ */

  function renderCounters() {
    var count = api.count();
    var label = count > 9 ? '9+' : String(count);

    each(document.querySelectorAll('[data-wishlist-count]'), function (element) {
      element.textContent = label;

      var toggle = element.closest('.wishlistToggle');
      if (toggle) toggle.classList.toggle('cartToggle--empty', count === 0);
    });
  }

  document.addEventListener(CHANGE_EVENT, renderCounters);
  document.addEventListener('DOMContentLoaded', renderCounters);
  renderCounters();

  /* ------------------------------------------------------------------ *
   * <pvj-wishlist-button>
   * ------------------------------------------------------------------ */

  class PVJWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-wishlist-toggle]');
      if (!this.button) return;

      this.handle = this.getAttribute('data-handle');
      this.productTitle = this.getAttribute('data-title') || '';

      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.button.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.sync);

      // Only the title-style button (beside the product page title) sits
      // next to a live variant picker. A grid tile's bookmark is rendered
      // once, already against the right variant, with nothing to track.
      //
      // Picking a swatch does not re-render <product-info> or touch its
      // [data-selected-variant] script - product-info.js patches the price,
      // media etc. into the live DOM directly and leaves that script at
      // whatever variant the page loaded with. It does, though, publish the
      // freshly selected variant on the theme's pubsub bus for exactly this
      // kind of listener, so that is the one reliable source of truth here.
      if (this.closest('product-info') && typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
        this.unsubscribeVariantChange = window.subscribe(
          window.theme.PUB_SUB_EVENTS.variantChange,
          this.onVariantChange
        );
      }

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.sync);
      if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }

    /* Keeps data-variant-id/-price/-image current with the swatch/option
       picker, rather than the values Liquid baked in for whichever variant
       the page happened to load with - see the note in connectedCallback. */
    onVariantChange(event) {
      var variant = event && event.data && event.data.variant;
      if (!variant || !variant.id) return;

      this.setAttribute('data-variant-id', variant.id);

      var image = variant.featured_image && variant.featured_image.src;
      if (image) this.setAttribute('data-image', resizeImage(image, POPOVER_IMAGE_WIDTH));

      var price = formatVariantPrice(variant.price);
      if (price) this.setAttribute('data-price', price);
    }

    onClick(event) {
      event.preventDefault();
      if (!isValidHandle(this.handle)) return;

      var variantId = this.getAttribute('data-variant-id');
      var saved = api.toggle(this.handle, {
        title: this.productTitle,
        variantId: variantId ? Number(variantId) : null
      });
      var name = this.productTitle || 'Item';

      announce(
        saved ? name + ' added to your wishlist' : name + ' removed from your wishlist'
      );
    }

    sync() {
      if (!this.button) return;

      var saved = api.has(this.handle);
      var label = this.button.getAttribute(saved ? 'data-label-added' : 'data-label-add');

      this.classList.toggle('is-saved', saved);
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  if (!window.customElements.get('pvj-wishlist-button')) {
    window.customElements.define('pvj-wishlist-button', PVJWishlistButton);
  }

  /* ------------------------------------------------------------------ *
   * Product tiles for the wishlist page
   *
   * Each saved piece is rendered by asking Shopify for the theme's own tile
   * markup on that product's URL, through sections/api-wishlist-item.liquid.
   * Tiles come back with live pricing, badges, swatches and quick view -
   * identical to a collection page, with no duplicated markup to keep in
   * sync. That section exists purely so the bookmark can be opted in here and
   * stay off every other grid in the store.
   * ------------------------------------------------------------------ */

  function tileUrl(handle, variantId) {
    var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';

    var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=' + TILE_SECTION_ID;
    // Puts the tile's price back in sync with whichever variant was selected
    // when the piece was saved, the same way the product page itself would
    // render at that URL. Shopify ignores the param quietly if the variant
    // no longer exists, falling back to the product's first available one.
    if (variantId) url += '&variant=' + encodeURIComponent(variantId);

    return url;
  }

  function fetchTile(handle, variantId) {
    return fetch(tileUrl(handle, variantId), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Unavailable');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var block = doc.querySelector('[data-api-content] [data-product-block]');
        var link = block && block.querySelector('[data-product-link]');

        // A handle that no longer resolves does not 404 here: Shopify renders
        // the section against an empty product and still returns 200. The
        // giveaway is an empty product URL. Treat that as unavailable so the
        // caller drops it, rather than showing a blank tile.
        if (!link || !link.getAttribute('href')) throw new Error('Unavailable');

        return document.importNode(block, true);
      });
  }

  function settleImage(image) {
    var done = function () {
      image.classList.remove('is-loading');
      var wrapper = image.closest('.lazy-image');
      if (wrapper) wrapper.classList.remove('is-loading');
    };

    if (image.complete && image.naturalWidth) {
      done();
      return;
    }

    image.addEventListener('load', done, { once: true });
    image.addEventListener('error', done, { once: true });
  }

  function prepareTile(node, handle) {
    node.setAttribute('data-wishlist-item', handle);

    // AOS entrance animations are wired up once on page load. A tile inserted
    // afterwards would keep its opacity: 0 start state forever, so drop the
    // animation hooks and let it render straight away.
    each(node.querySelectorAll('[data-aos]'), function (element) {
      element.removeAttribute('data-aos');
      element.removeAttribute('data-aos-delay');
      element.removeAttribute('data-aos-anchor');
    });

    // The theme clears `is-loading` as each image loads. Images inserted after
    // page load may already be complete by the time they land in the document.
    each(node.querySelectorAll('img.is-loading'), settleImage);

    return node;
  }

  // Fetch with a small concurrency cap, keeping results in the requested
  // order. variantLookup maps handle -> variant id, for handles that were
  // saved against a specific variant (see PVJWishlistPage.buildVariantLookup).
  function fetchTiles(handles, variantLookup) {
    var results = new Array(handles.length);
    var cursor = 0;

    function next() {
      if (cursor >= handles.length) return Promise.resolve();

      var index = cursor++;
      var handle = handles[index];

      return fetchTile(handle, variantLookup && variantLookup[handle])
        .then(function (node) {
          results[index] = node;
        })
        .catch(function () {
          results[index] = null;
        })
        .then(next);
    }

    var workers = [];
    for (var i = 0; i < Math.min(FETCH_CONCURRENCY, handles.length); i++) {
      workers.push(next());
    }

    return Promise.all(workers).then(function () {
      return results;
    });
  }

  /* ------------------------------------------------------------------ *
   * <pvj-wishlist-page>
   * ------------------------------------------------------------------ */

  class PVJWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      if (!this.grid) return;

      this.bar = this.querySelector('[data-wishlist-bar]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this.countText = this.querySelector('[data-wishlist-count-text]');
      this.sharedBanner = this.querySelector('[data-wishlist-shared-banner]');
      this.shareToggle = this.querySelector('[data-wishlist-share-toggle]');
      this.shareFeedback = this.querySelector('[data-wishlist-share-feedback]');

      this.token = 0;
      this.pruning = false;
      this.sharedHandles = this.readSharedHandles();

      this.onChange = this.onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this.onChange);

      this.bindControls();

      if (this.sharedHandles && this.sharedBanner) {
        this.sharedBanner.hidden = false;
      }

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
      window.clearTimeout(this.shareFeedbackTimer);
      window.clearTimeout(this.fadeTimer);
      window.clearTimeout(this.resizeTimer);
    }

    /* --- shared wishlists (?list=handle-a,handle-b) --- */

    readSharedHandles() {
      var raw;

      try {
        raw = new URLSearchParams(window.location.search).get('list');
      } catch (error) {
        return null;
      }

      if (!raw) return null;

      var handles = raw
        .split(',')
        .map(function (handle) {
          return handle.trim();
        })
        .filter(isValidHandle)
        .slice(0, MAX_ITEMS);

      return handles.length ? handles : null;
    }

    /* --- rendering --- */

    onChange() {
      if (this.pruning) return;

      if (this.sharedHandles) {
        this.updateMeta();
        return;
      }

      this.sync();
    }

    // Handle -> variant id, for pieces saved against a specific variant
    // (title-style bookmark). A shared ?list= link carries handles only, so
    // there is nothing to look up there - the recipient sees the product's
    // default variant, same as opening the link fresh always has.
    buildVariantLookup() {
      var lookup = {};

      api.items().forEach(function (item) {
        if (item.variantId) lookup[item.handle] = item.variantId;
      });

      return lookup;
    }

    sync() {
      var handles = this.sharedHandles || api.handles();
      var variantLookup = this.sharedHandles ? null : this.buildVariantLookup();
      var rendered = {};

      each(this.grid.querySelectorAll('[data-wishlist-item]'), function (element) {
        rendered[element.getAttribute('data-wishlist-item')] = element;
      });

      var removedHandles = Object.keys(rendered).filter(function (handle) {
        return handles.indexOf(handle) === -1;
      });

      // Usually a tile just un-saved - fade it out rather than cutting it
      // instantly, then carry on with whatever else changed once it's gone.
      if (removedHandles.length) {
        this.fadeOutRemoved(removedHandles, rendered, handles, variantLookup);
        return;
      }

      this.reconcile(handles, rendered, variantLookup);
    }

    fadeOutRemoved(removedHandles, rendered, handles, variantLookup) {
      var self = this;

      removedHandles.forEach(function (handle) {
        var tile = rendered[handle];
        if (tile) tile.classList.add('is-removing');
      });

      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = window.setTimeout(function () {
        removedHandles.forEach(function (handle) {
          var tile = rendered[handle];
          if (tile && tile.parentNode) tile.parentNode.removeChild(tile);
          delete rendered[handle];
        });

        if (handles.length === 0) {
          self.fadeToEmpty();
          return;
        }

        self.reconcile(handles, rendered, variantLookup);
      }, TILE_FADE_MS);
    }

    // The last piece just faded out - crossfade the toolbar out and the
    // empty state in, rather than the [data-state] swap in commit() cutting
    // straight from the (now-empty) grid to "Nothing saved yet". Also
    // animates the section's own height across that swap, so everything
    // below it (the "Back" link, the footer) glides up to its new resting
    // position instead of jumping there the instant [data-state] flips.
    fadeToEmpty() {
      var self = this;
      var app = this;
      var startHeight = app.getBoundingClientRect().height;

      if (this.bar) this.bar.classList.add('is-fading-out');

      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = window.setTimeout(function () {
        app.classList.add('is-resizing');
        app.style.height = startHeight + 'px';
        // Commits the start height before anything below changes, so the
        // transition below has a fixed point to animate away from.
        void app.offsetHeight;

        self.setAttribute('data-state', 'empty');
        self.updateMeta([]);

        if (self.bar) self.bar.classList.remove('is-fading-out');

        if (self.emptyEl) {
          self.emptyEl.classList.add('is-fading-in');
          void self.emptyEl.offsetHeight;
          self.emptyEl.classList.remove('is-fading-in');
        }

        app.style.height = (self.emptyEl ? self.emptyEl.offsetHeight : 0) + 'px';

        window.clearTimeout(self.resizeTimer);
        self.resizeTimer = window.setTimeout(function () {
          app.classList.remove('is-resizing');
          app.style.height = '';
        }, RESIZE_MS);
      }, EMPTY_FADE_OUT_MS);
    }

    reconcile(handles, rendered, variantLookup) {
      var self = this;
      var toFetch = handles.filter(function (handle) {
        return !rendered[handle];
      });

      if (!toFetch.length) {
        this.commit(handles, rendered);
        return;
      }

      var token = ++this.token;
      if (!Object.keys(rendered).length) this.setAttribute('data-state', 'loading');

      fetchTiles(toFetch, variantLookup).then(function (nodes) {
        if (token !== self.token) return;

        var unavailable = [];

        nodes.forEach(function (node, index) {
          var handle = toFetch[index];

          if (!node) {
            unavailable.push(handle);
            return;
          }

          rendered[handle] = prepareTile(node, handle);
        });

        if (unavailable.length) {
          if (self.sharedHandles) {
            handles = handles.filter(function (handle) {
              return unavailable.indexOf(handle) === -1;
            });
          } else {
            // A saved piece that no longer resolves has been deleted or
            // unpublished. Quietly forget it rather than show a broken tile.
            self.pruning = true;
            unavailable.forEach(function (handle) {
              api.remove(handle);
            });
            self.pruning = false;
            handles = api.handles();
          }
        }

        self.commit(handles, rendered);
      });
    }

    commit(handles, rendered) {
      var grid = this.grid;

      // Re-appending an existing child moves it, so this both inserts new tiles
      // and puts everything back into wishlist order (newest first).
      handles.forEach(function (handle) {
        if (rendered[handle]) grid.appendChild(rendered[handle]);
      });

      this.setAttribute('data-state', handles.length ? 'ready' : 'empty');
      this.updateMeta(handles);
    }

    updateMeta(handles) {
      var list = handles || this.sharedHandles || api.handles();
      var count = list.length;

      if (this.countText) {
        this.countText.textContent =
          count === 1 ? '1 item saved' : count + ' items saved';
      }

      this.shareUrl =
        window.location.origin +
        window.location.pathname +
        (count ? '?list=' + list.join(',') : '');
    }

    /* --- controls --- */

    bindControls() {
      var self = this;

      if (this.shareToggle) {
        this.shareToggle.addEventListener('click', function () {
          self.copyShareUrl();
        });
      }
    }

    copyShareUrl() {
      var self = this;
      var url = this.shareUrl;
      if (!url || !this.shareToggle || !this.shareFeedback) return;

      var showFeedback = function () {
        self.shareToggle.hidden = true;
        self.shareFeedback.hidden = false;
        announce('Link copied');

        window.clearTimeout(self.shareFeedbackTimer);
        self.shareFeedbackTimer = window.setTimeout(function () {
          self.shareToggle.hidden = false;
          self.shareFeedback.hidden = true;
        }, 2400);
      };

      var fallback = function () {
        var input = document.createElement('input');
        input.value = url;
        input.setAttribute('readonly', 'readonly');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        input.setSelectionRange(0, input.value.length);

        try {
          document.execCommand('copy');
          showFeedback();
        } catch (error) {
          // Nothing else to try. The clipboard just will not update.
        }

        document.body.removeChild(input);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(showFeedback, fallback);
      } else {
        fallback();
      }
    }
  }

  if (!window.customElements.get('pvj-wishlist-page')) {
    window.customElements.define('pvj-wishlist-page', PVJWishlistPage);
  }
})();
